import { useEffect, useState } from "react";
import { Button } from "../../components/Button";
import { PageHeader } from "../../components/PageHeader";
import { Sheet } from "../../components/Sheet";
import { Input } from "../../components/Input";
import { Toast } from "../../components/Toast";
import { CheckIcon, PencilIcon, PlusIcon, RulerIcon } from "../../components/icons";
import { useCreateSizeGuide, useSizeGuides, useUpdateSizeGuide } from "./hooks";
import type { SizeGuide } from "./api";

export function AdminSizeGuidesView() {
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<SizeGuide | "new" | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const guides = useSizeGuides(showInactive ? undefined : true);
  const update = useUpdateSizeGuide();

  return (
    <main className="page page--narrow">
      <PageHeader title="Guías de talles" backHref="/more" actions={<Button onClick={() => setEditing("new")}><PlusIcon /> Nueva</Button>} />
      <section className="page-section">
        <div className="section-intro">
          <div><p className="eyebrow">Catálogo</p><h1>Talles reutilizables</h1><p>Definí las opciones una sola vez y asocialas a todos los productos que correspondan.</p></div>
          <label className="switch-row"><input type="checkbox" checked={showInactive} onChange={(event) => setShowInactive(event.target.checked)} /> Ver inactivas</label>
        </div>
        {guides.isLoading && <div className="skeleton-list"><span /><span /><span /></div>}
        {guides.data?.length === 0 && <div className="empty-state"><RulerIcon width={26} height={26} /><strong>Sin guías de talles</strong><span>Creá una guía para asociarla a tus productos.</span></div>}
        <div className="entity-list">
          {guides.data?.map((guide) => (
            <article className={`entity-row${guide.active ? "" : " is-inactive"}`} key={guide.id}>
              <span className="entity-row__icon"><RulerIcon /></span>
              <div className="entity-row__body">
                <strong>{guide.name}</strong>
                <small>{guide.values.join(" · ")} · {guide._count.products} {guide._count.products === 1 ? "producto" : "productos"}</small>
              </div>
              <span className={`status-badge ${guide.active ? "status-badge--success" : "status-badge--neutral"}`}>{guide.active ? "Activa" : "Inactiva"}</span>
              <button className="icon-action" type="button" aria-label={`Editar ${guide.name}`} onClick={() => setEditing(guide)}><PencilIcon /></button>
              <button className="text-action" type="button" onClick={async () => {
                await update.mutateAsync({ id: guide.id, body: { active: !guide.active } });
                setFeedback(guide.active ? "Guía desactivada" : "Guía activada");
              }}>{guide.active ? "Desactivar" : "Activar"}</button>
            </article>
          ))}
        </div>
      </section>
      <SizeGuideSheet target={editing} onClose={() => setEditing(null)} onSaved={(message) => { setEditing(null); setFeedback(message); }} />
      {feedback && <Toast kind="success" message={feedback} icon={<CheckIcon />} autoCloseMs={2400} onDismiss={() => setFeedback(null)} />}
    </main>
  );
}

function SizeGuideSheet({ target, onClose, onSaved }: { target: SizeGuide | "new" | null; onClose: () => void; onSaved: (message: string) => void }) {
  const create = useCreateSizeGuide();
  const update = useUpdateSizeGuide();
  const [name, setName] = useState("");
  const [valuesText, setValuesText] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(target && target !== "new" ? target.name : "");
    setValuesText(target && target !== "new" ? target.values.join(", ") : "");
    setError(null);
  }, [target]);

  if (!target) return null;
  const parseValues = () => valuesText.split(/[,\n]/).map((value) => value.trim()).filter(Boolean);

  return (
    <Sheet open title={target === "new" ? "Nueva guía de talles" : "Editar guía de talles"} onClose={onClose}>
      <form className="form-stack" onSubmit={async (event) => {
        event.preventDefault();
        const values = parseValues();
        if (name.trim().length < 2) return setError("Ingresá un nombre para la guía.");
        if (values.length === 0) return setError("Ingresá al menos un talle.");
        try {
          const payload = { name: name.trim(), values };
          if (target === "new") await create.mutateAsync(payload);
          else await update.mutateAsync({ id: target.id, body: payload });
          onSaved(target === "new" ? "Guía creada" : "Guía actualizada");
        } catch {
          setError("No se pudo guardar. Revisá que el nombre no esté repetido.");
        }
      }}>
        <Input label="Nombre de la guía" placeholder="Ej. Letras" value={name} onChange={(event) => { setName(event.target.value); setError(null); }} autoFocus />
        <label className="field-control">
          <span>Talles</span>
          <textarea rows={4} placeholder="S, M, L, XL, XXL" value={valuesText} onChange={(event) => { setValuesText(event.target.value); setError(null); }} />
        </label>
        <p className="field-help">Separalos con comas o saltos de línea. Se mostrarán en este orden.</p>
        {valuesText.trim() && <div className="size-value-preview" aria-label="Vista previa de talles">{parseValues().map((value, index) => <span key={`${value}-${index}`}>{value}</span>)}</div>}
        {error && <span className="form-error" role="alert">{error}</span>}
        <Button type="submit" fullWidth size="lg" disabled={create.isPending || update.isPending}>{create.isPending || update.isPending ? "Guardando…" : "Guardar guía"}</Button>
      </form>
    </Sheet>
  );
}
